import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { LinkProps } from "@tanstack/react-router";


export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div
      className="mono eyebrow"
      style={{
        fontSize: 11,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "rgba(0,47,167,0.45)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginBottom: 28,
      }}
    >
      <span style={{ width: 32, height: 1, background: "#8a6f3f" }} />
      <span>{children}</span>
    </div>
  );
}

export function SectionNum({ children }: { children: ReactNode }) {
  return <div className="section-num">{children}</div>;
}

export function DisplayHeading({
  children,
  className = "",
  as: As = "h1",
}: {
  children: ReactNode;
  className?: string;
  as?: "h1" | "h2";
}) {
  return (
    <As
      className={className}
      style={{
        fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
        fontWeight: 200,
        fontSize: As === "h1" ? "clamp(40px, 6.5vw, 88px)" : "clamp(32px, 4.6vw, 60px)",
        lineHeight: 1.02,
        letterSpacing: "-0.03em",
        margin: 0,
        color: "#002FA7",
      }}
    >
      {children}
    </As>
  );
}

export function Lede({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={`prose-lead ${className}`}
      style={{
        fontSize: 16.5,
        color: "rgba(0,47,167,0.74)",
        maxWidth: "52ch",
        lineHeight: 1.55,
        margin: 0,
      }}
    >
      {children}
    </p>
  );
}

type BtnProps = {
  to?: string;
  href?: string;
  children: ReactNode;
  variant?: "primary" | "ghost";
  onClick?: () => void;
  type?: "button" | "submit";
  disabled?: boolean;
  className?: string;
};

const baseBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  borderRadius: 4,
  fontFamily: '"IBM Plex Mono", "IBM Plex Mono Fallback", monospace',
  fontSize: 11.5,
  fontWeight: 500,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  cursor: "pointer",
  border: "1px solid transparent",
  lineHeight: 1,
  transition: "transform .15s ease, background .15s ease, color .15s ease, border-color .15s ease",
};

const primaryStyle: React.CSSProperties = {
  ...baseBtn,
  background: "#8a6f3f",
  color: "#ffffff",
};

const ghostStyle: React.CSSProperties = {
  ...baseBtn,
  background: "transparent",
  color: "#002FA7",
  borderColor: "rgba(0,47,167,0.26)",
};

export function Btn({
  to,
  href,
  children,
  variant = "primary",
  onClick,
  type = "button",
  disabled,
  className = "",
}: BtnProps) {
  const style = variant === "primary" ? primaryStyle : ghostStyle;
  if (to) {
    return (
      <Link to={to} className={`bhg-btn ${className}`} style={style} onClick={onClick}>
        {children}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} className={`bhg-btn ${className}`} style={style}>
        {children}
      </a>
    );
  }
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`bhg-btn ${className}`}
      style={style}
    >
      {children}
    </button>
  );
}

export function SectionHeader({
  num,
  title,
  children,
}: {
  num: string;
  title: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className={`max-w-3xl section-header ${children ? "section-header-intro" : ""}`}>
      <SectionNum>{num}</SectionNum>
      <DisplayHeading as="h2">{title}</DisplayHeading>
      {children ? (
        <div className="mt-6">
          <Lede>{children}</Lede>
        </div>
      ) : null}
    </div>
  );
}
